import React, { useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';

const rawScriptUrl = process.env.NEXT_PUBLIC_WIDGET_SCRIPT_URL || '/js/widget.iife.js';
const WIDGET_SCRIPT_URL = rawScriptUrl.includes('digitaloceanspaces.com')
  ? '/js/widget.iife.js'
  : rawScriptUrl;

export function MissiAiPublicWidget() {
  return <MissiAiWidget forceMode="public" />;
}

export function MissiAiAuthWidget() {
  return <MissiAiWidget forceMode="auth" />;
}

interface MissiAiWidgetProps {
  forceMode?: 'public' | 'auth';
}

function checkIsAllowed(pathname: string): boolean {
  const cleanPath = (pathname || '').replace(/\/+$/, '') || '/';
  if (cleanPath.includes('embed') || cleanPath.startsWith('/apps-embed')) {
    return false;
  }
  const isAppsPage = cleanPath === '/apps' || cleanPath.startsWith('/apps/');
  return isAppsPage;
}

export default function MissiAiWidget({ forceMode }: MissiAiWidgetProps = {}) {
  let location: any = { pathname: '/' };
  try {
    location = useLocation();
  } catch (e) {
    // Fallback if outside router context
  }

  const lastInitializedModeRef = useRef<'auth' | 'public' | null>(null);
  const lastTokenRef = useRef<string | null>(null);

  const cleanPath = (location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '')).replace(/\/+$/, '') || '/';
  const isAllowedRoute = checkIsAllowed(cleanPath);
  const isAppsPage = cleanPath === '/apps' || cleanPath.startsWith('/apps/');
  const isAppRoute = forceMode === 'auth' || (forceMode !== 'public' && isAppsPage);
  const targetMode: 'auth' | 'public' = isAppRoute ? 'auth' : 'public';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const setWidgetVisibility = (visible: boolean) => {
      if (visible) {
        try {
          (window as any).ChatWidget?.showWidget?.();
        } catch (e) {}
        const widgetEls = document.querySelectorAll(
          '#missi-widget-root, #missi-widget-mount, #missi-launcher-mount, #chat-widget-container, .chat-widget-container, div[id*="chat-widget"], div[class*="chat-widget"], div[id*="missi-widget"]'
        );
        widgetEls.forEach((el) => {
          (el as HTMLElement).style.removeProperty('display');
        });
      } else {
        try {
          (window as any).ChatWidget?.minimize?.();
          (window as any).ChatWidget?.hideWidget?.();
        } catch (e) {}
        const widgetEls = document.querySelectorAll(
          '#missi-widget-root, #missi-widget-mount, #missi-launcher-mount, #chat-widget-container, .chat-widget-container, div[id*="chat-widget"], div[class*="chat-widget"], div[id*="missi-widget"]'
        );
        widgetEls.forEach((el) => {
          (el as HTMLElement).style.setProperty('display', 'none', 'important');
        });
      }
    };

    // If current route is NOT allowed, hide the widget and do not initialize
    if (!isAllowedRoute) {
      setWidgetVisibility(false);
      return;
    }

    const token = targetMode === 'auth' ? localStorage.getItem('_missi_token') || undefined : undefined;

    const initWidget = () => {
      // Re-verify route in case navigation occurred during async script load
      const currentWindowPath = (window.location.pathname || '').replace(/\/+$/, '') || '/';
      if (!checkIsAllowed(currentWindowPath)) {
        setWidgetVisibility(false);
        return;
      }

      if ((window as any).ChatWidget) {
        if (targetMode === 'auth') {
          (window as any).ChatWidget.init({
            user: token,
            onLoad: () => console.log('[MISSI-AI-WIDGET] Initialized in auth token mode'),
          });
        } else {
          // Public non-token mode for landing page / customer service
          (window as any).ChatWidget.init({
            knowledge: 'customer_service',
            onLoad: () => console.log('[MISSI-AI-WIDGET] Initialized in public non-token mode'),
          });
        }
        lastInitializedModeRef.current = targetMode;
        lastTokenRef.current = token || null;
        setWidgetVisibility(true);
      }
    };

    // If widget already loaded:
    if ((window as any).ChatWidget) {
      // If already initialized with matching mode and token, simply make sure it's visible
      if (
        lastInitializedModeRef.current === targetMode &&
        (targetMode !== 'auth' || lastTokenRef.current === (token || null))
      ) {
        setWidgetVisibility(true);
        return;
      }
      // Otherwise (mode or token changed), re-init
      initWidget();
      return;
    }

    let script = document.getElementById('missi-ai-widget-script') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'missi-ai-widget-script';
      script.src = WIDGET_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        initWidget();
      };
      script.onerror = () => {
        if (WIDGET_SCRIPT_URL !== '/js/widget.iife.js') {
          console.warn(`[MISSI-AI-WIDGET] Failed to load widget from ${WIDGET_SCRIPT_URL}, attempting fallback /js/widget.iife.js`);
          const fallbackScript = document.createElement('script');
          fallbackScript.id = 'missi-ai-widget-script-fallback';
          fallbackScript.src = '/js/widget.iife.js';
          fallbackScript.async = true;
          fallbackScript.onload = () => initWidget();
          document.head.appendChild(fallbackScript);
        }
      };
      document.head.appendChild(script);
    } else {
      script.addEventListener('load', initWidget);
    }

    return () => {
      if (script) {
        script.removeEventListener('load', initWidget);
      }
    };
  }, [cleanPath, isAllowedRoute, targetMode, forceMode]);

  return null;
}
